import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabase';
import { Toast } from './Toast';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'info' | 'success' | 'error';
}

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, string>>({});

  useEffect(() => {
    // Load user profiles for display names
    const loadUserProfiles = async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('user_id, display_name');
      
      if (data) {
        const profiles = data.reduce((acc, profile) => ({
          ...acc,
          [profile.user_id]: profile.display_name
        }), {});
        setUserProfiles(profiles);
      }
    };
    loadUserProfiles();

    const channel = supabase.channel('db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
        },
        async (payload) => {
          const timestamp = new Date().getTime();
          let message = '';
          let type: 'info' | 'success' | 'error' = 'info';

          const getUserName = (userId: string) => {
            return userProfiles[userId] || 'Someone';
          };

          switch (payload.table) {
            case 'time_records':
              if (payload.eventType === 'INSERT') {
                const userName = getUserName(payload.new.user_id);
                message = `${userName} added a time record for ${new Date(payload.new.record_date).toLocaleDateString()}`;
                type = 'success';
              } else if (payload.eventType === 'UPDATE') {
                const userName = getUserName(payload.new.user_id);
                message = `${userName} updated their time record for ${new Date(payload.new.record_date).toLocaleDateString()}`;
                type = 'success';
              } else if (payload.eventType === 'DELETE') {
                const userName = getUserName(payload.old.user_id);
                message = `${userName} deleted their time record for ${new Date(payload.old.record_date).toLocaleDateString()}`;
                type = 'info';
              }
              break;

            case 'user_profiles':
              if (payload.eventType === 'UPDATE') {
                const userName = getUserName(payload.new.user_id);
                message = `${userName} updated their profile`;
                type = 'success';
              }
              break;

            case 'profile_photos':
              if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                const userName = getUserName(payload.new.user_id);
                message = `${userName} updated their profile photo`;
                type = 'success';
              } else if (payload.eventType === 'DELETE') {
                const userName = getUserName(payload.old.user_id);
                message = `${userName} removed their profile photo`;
                type = 'info';
              }
              break;

            case 'gcash_numbers':
              if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
                const userName = getUserName(payload.new.user_id);
                message = `${userName} updated their GCash number`;
                type = 'success';
              } else if (payload.eventType === 'DELETE') {
                const userName = getUserName(payload.old.user_id);
                message = `${userName} removed their GCash number`;
                type = 'info';
              }
              break;
          }

          if (message) {
            setToasts(prev => [...prev, {
              id: `${timestamp}`,
              message,
              type
            }]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userProfiles]);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return createPortal(
    <div className="fixed inset-x-0 top-4 z-50 flex flex-col items-center gap-2 pointer-events-none">
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto w-full max-w-sm mx-auto px-4">
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        </div>
      ))}
    </div>,
    document.body
  );
};

export default ToastContainer;